import {
  handleUrl,
  myHeadersWithToken,
  myHeadersWithTokenContentType,
} from '../../global';
import {
  AddLicenseProps,
  AddLicenseResponse,
  ApproveWithReservationData,
  CancelExtraDocumentData,
  CreatePublicNoticeData,
  CreatePublicNoticeResponse,
  DetailProps,
  GetAcceptanceDocsResponse,
  GetCriteriaDocumentsResponse,
  GetDocumentStatusHistoryResponse,
  GetNoticeCompanyDocumentsResponse,
  GetNoticeCompanyResponse,
  GetAdministrativeTermsResponse,
  GetNoticeCompanyTeamParams,
  GetNoticeCompanyTeamResponse,
  GetNoticeData,
  GetNoticesWorkOrdersResponse,
  ListNoticeAttachments,
  ListNoticeCompaniesParams,
  ListChangeRequestsParams,
  ListChangeRequestsResponse,
  ListNoticeCompaniesResponse,
  ListNoticesProps,
  RejectDocumentData,
  UpdatePublicNoticeData,
} from './types';

export const createPublicNotice = (
  data: CreatePublicNoticeData
): Promise<CreatePublicNoticeResponse> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/public-notices`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listPublicNotices = (
  page: number,
  size: number,
  kind: string,
  order: string,
  search: string
): Promise<ListNoticesProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();

  const params: Record<string, string> = {
    page: String(page),
    size: String(size),
    order_by: kind,
    order,
    search,
  };

  const queryString = new URLSearchParams(params).toString();

  return fetch(
    `${baseUrl}/api/V1/public-notices?${queryString}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNotice = (id: number): Promise<GetNoticeData> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/public-notices/${id}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const updatePublicNotice = (
  id: number,
  data: UpdatePublicNoticeData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/public-notices/${id}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const deletePublicNotice = (id: number): Promise<DetailProps> => {
  const requestOptions = {
    method: 'DELETE',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/public-notices/${id}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listPublicNoticeAttachment = (
  noticeId: number,
  page: number,
  size: number,
  type?: string
): Promise<ListNoticeAttachments> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };

  const params: Record<string, string> = {
    page: String(page),
    size: String(size),
  };
  if (type) params.attachment_type = type;

  const queryString = new URLSearchParams(params).toString();
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/attachments?${queryString}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const createPublicNoticeAttachment = (
  noticeId: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/attachments`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const editPublicNoticeAttachment = (
  attId: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/notice-attachments/${attId}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const deletePublicNoticeAttachment = (
  fileId: number
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'DELETE',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/notice-attachments/${fileId}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const addLicense = (
  notice: number,
  data: AddLicenseProps
): Promise<AddLicenseResponse> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${notice}/licensed-companies`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listNoticeCompanies = ({
  publicNoticeId,
  page,
  size,
  search,
  approvalStatus,
  pendingSelfRequest,
  pendingUserRequest,
  pendingRemovalRequest,
  scope,
  orderBy,
}: ListNoticeCompaniesParams): Promise<ListNoticeCompaniesResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    search,
  });

  if (approvalStatus) {
    approvalStatus.forEach((status) => {
      params.append('approval_status', status);
    });
  }
  if (pendingSelfRequest)
    params.append('pending_self_request', pendingSelfRequest);
  if (pendingUserRequest)
    params.append('pending_user_request', pendingUserRequest);
  if (pendingRemovalRequest)
    params.append('pending_removal_request', pendingRemovalRequest);
  if (scope) params.append('scope', scope);
  if (orderBy) params.append('order_by', orderBy);

  const queryString = params.toString();

  const baseUrl = handleUrl();
  return fetch(
    // eslint-disable-next-line max-len
    `${baseUrl}/api/V1/public-notices/${publicNoticeId}/licensed-companies?${queryString}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNoticesWorkOrders = (
  id: number,
  page: number,
  size: number
): Promise<GetNoticesWorkOrdersResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/work-orders?page=${page}&size=${size}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNoticeCompany = (
  id: number
): Promise<GetNoticeCompanyResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/notice-companies/${id}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNoticeCompanyDocuments = (
  id: number,
  noticeCompanyUserId?: number
): Promise<GetNoticeCompanyDocumentsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  // Without the param the endpoint returns only the company documents; with it,
  // only the ones owned by that professional's individual accreditation.
  const params = new URLSearchParams();
  if (noticeCompanyUserId)
    params.append('notice_company_user_id', String(noticeCompanyUserId));

  const queryString = params.toString();
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents${
      queryString ? `?${queryString}` : ''
    }`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNoticeCompanyTerms = (
  noticeCompanyId: number
): Promise<GetAdministrativeTermsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${noticeCompanyId}/terms`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getNoticeCompanyTeam = ({
  noticeCompanyId,
  status,
  search,
}: GetNoticeCompanyTeamParams): Promise<GetNoticeCompanyTeamResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  const params = new URLSearchParams();
  status?.forEach((item) => params.append('status', item));
  if (search) params.append('search', search);

  const queryString = params.toString();
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${noticeCompanyId}/team${
      queryString ? `?${queryString}` : ''
    }`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getNoticeCompanyDocumentStatusHistory = (
  id: number,
  documentId: number,
  status?: number
): Promise<GetDocumentStatusHistoryResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const params = new URLSearchParams();
  if (status !== undefined) params.append('status', String(status));
  const queryString = params.toString();
  const baseUrl = handleUrl();
  return fetch(
    // eslint-disable-next-line max-len
    `${baseUrl}/api/V1/notice-companies/${id}/documents/${documentId}/status-history${
      queryString ? `?${queryString}` : ''
    }`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listNoticeCompanyChangeRequests = ({
  noticeCompanyId,
  noticeCompanyUserId,
  page,
  size,
  status,
  requestType,
}: ListChangeRequestsParams): Promise<ListChangeRequestsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  // Same owner scope as GET /documents and /criteria-documents: without the
  // param the listing is the COMPANY's (owner NULL) and the professionals'
  // own requests are left out, so each one has to be asked for by id.
  const params = new URLSearchParams();
  if (noticeCompanyUserId)
    params.append('notice_company_user_id', String(noticeCompanyUserId));
  if (page) params.append('page', String(page));
  if (size) params.append('size', String(size));
  status?.forEach((item) => params.append('status', String(item)));
  requestType?.forEach((item) => params.append('request_type', String(item)));

  const queryString = params.toString();
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${noticeCompanyId}/change-requests${
      queryString ? `?${queryString}` : ''
    }`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getNoticeCompanyCriteriaDocuments = (
  id: number,
  noticeCompanyUserId?: number
): Promise<GetCriteriaDocumentsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  // Same owner scope as GET /documents: without the param the endpoint
  // returns the company's groups and documents (which is also what the
  // responsible's seed record resolves to); with it, that professional's own.
  const params = new URLSearchParams();
  if (noticeCompanyUserId)
    params.append('notice_company_user_id', String(noticeCompanyUserId));

  const queryString = params.toString();
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/criteria-documents${
      queryString ? `?${queryString}` : ''
    }`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const approveNoticeCompanyDocument = (
  id: number,
  docId: number
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents/${docId}/approve`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const approveNoticeCompanyDocumentWithReservation = (
  id: number,
  docId: number,
  data: ApproveWithReservationData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents/${docId}/approve-with-reservations`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const rejectNoticeCompanyDocument = (
  id: number,
  docId: number,
  data: RejectDocumentData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents/${docId}/reject`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const replaceNoticeCompanyDocument = (
  id: number,
  docId: number
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents/${docId}/request-replacement`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const requestNoticeCompanyDocument = (
  id: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/documents?send_notification=true`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const cancelExtraDocument = (
  data: CancelExtraDocumentData
): Promise<DetailProps> => {
  const { documentId, noticeCompanyId } = data;
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${noticeCompanyId}/documents/${documentId}/cancel`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const addContractDocument = (
  noticeId: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/contract`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const addAcceptanceDocuments = (
  noticeId: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/acceptance-documents`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listAcceptanceDocuments = (
  noticeId: number
): Promise<GetAcceptanceDocsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/acceptance-documents`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const approveContractDocument = (id: number): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/approve-formalization`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const rejectContractDocument = (
  id: number,
  data: RejectDocumentData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/notice-companies/${id}/reject-formalization`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};
